import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { LoadingList, LoadingListItem, ParsedGroup, RawColumn } from '../types'
import { inputCls, btnPrimary, btnSecondary, fmtDate } from '../types'
import ColumnMappingTable, { type MappingTarget, suggestMapping } from '../../../components/ColumnMappingTable'
import SmartDevanSplitDialog, { applySmartSplit, type SplitConfig } from '../../../components/SmartDevanSplitDialog'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { useToast } from '../../../hooks/useToast'

const EMPTY_ITEM: LoadingListItem = {
  hbl_no: '', job_ref: null, split_no: null, container_no: null,
  client_ref: '', customer_name: '', description: '',
  system_ref: null, expected_cartons: 1, expected_weight_kg: null,
  expected_cbm: null, net_weight_kg: null,
  delivery_address: '', delivery_postcode: '', delivery_contact: '',
  delivery_method: null, delivery_tracking_no: null,
  shipment_id: null, amazon_ref_id: null,
  vat_number: null, appointment_code: null, physical_warehouse: null,
  group_id: null, notes: '',
}

const collapseKey = (id: string | null) => id ?? '__unassigned__'

// ─── Column definitions ──────────────────────────────────────────────────────

type ColDef = {
  key: string
  label: string
  sortable: keyof LoadingListItem | null
  type: 'str' | 'num'
  field: keyof LoadingListItem
  step?: string
  wrap?: true
}

const ALL_COL_DEFS: ColDef[] = [
  { key: 'hbl_no',               label: 'HBL No.',         sortable: 'hbl_no',           type: 'str', field: 'hbl_no' },
  { key: 'job_ref',              label: 'Job Ref',          sortable: 'job_ref',          type: 'str', field: 'job_ref' },
  { key: 'split_no',             label: 'Split No',         sortable: null,               type: 'str', field: 'split_no' },
  { key: 'system_ref',           label: 'N/M',              sortable: null,               type: 'str', field: 'system_ref' },
  { key: 'description',          label: 'Description',      sortable: null,               type: 'str', field: 'description',          wrap: true },
  { key: 'delivery_address',     label: 'Delivery Address', sortable: 'delivery_address', type: 'str', field: 'delivery_address',     wrap: true },
  { key: 'delivery_postcode',    label: 'Postcode',         sortable: null,               type: 'str', field: 'delivery_postcode' },
  { key: 'delivery_contact',     label: 'Contact',          sortable: null,               type: 'str', field: 'delivery_contact',     wrap: true },
  { key: 'delivery_method',      label: 'Method',           sortable: null,               type: 'str', field: 'delivery_method' },
  { key: 'delivery_tracking_no', label: 'Tracking',         sortable: null,               type: 'str', field: 'delivery_tracking_no' },
  { key: 'vat_number',           label: 'VAT',              sortable: null,               type: 'str', field: 'vat_number' },
  { key: 'appointment_code',     label: 'Appt',             sortable: null,               type: 'str', field: 'appointment_code' },
  { key: 'expected_cartons',     label: 'Ctns',             sortable: 'expected_cartons', type: 'num', field: 'expected_cartons', step: '1' },
  { key: 'expected_weight_kg',   label: 'Wt (kg)',          sortable: null,               type: 'num', field: 'expected_weight_kg', step: '0.001' },
  { key: 'expected_cbm',         label: 'CBM',              sortable: null,               type: 'num', field: 'expected_cbm', step: '0.001' },
  { key: 'notes',                label: 'Note / Remarks',   sortable: null,               type: 'str', field: 'notes',                wrap: true },
]

const DEFAULT_HIDDEN_COLS = ['delivery_contact', 'vat_number', 'appointment_code']

// ─── AI Parse Preview Dialog ─────────────────────────────────────────────────

interface ParseResult {
  items: LoadingListItem[]
  format_detected: string
  skipped_rows: number
}

function ParsePreviewDialog({ result, onConfirm, onClose }: {
  result: ParseResult
  onConfirm: (items: LoadingListItem[]) => void
  onClose: () => void
}) {
  const [items, setItems] = useState<LoadingListItem[]>(result.items)
  const update = (idx: number, k: keyof LoadingListItem, v: unknown) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it))
  const remove = (idx: number) =>
    setItems(prev => prev.filter((_, i) => i !== idx))

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">AI Parse Preview</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Format <strong>{result.format_detected}</strong> · {items.length} items · {result.skipped_rows} rows skipped
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-auto flex-1 p-4">
          <table className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                {['#', 'HBL No', 'Job Ref', 'Split No', 'N/M', 'Ctns', 'Wt kg', 'CBM',
                  'Method', 'Tracking', 'Address', 'Postcode', 'Amazon Ref', 'VAT', 'Appt', ''].map(h => (
                  <th key={h} className="px-2 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1 text-gray-400">{idx + 1}</td>
                  {(['hbl_no', 'job_ref', 'split_no', 'system_ref'] as const).map(f => (
                    <td key={f} className="px-1 py-1">
                      <input className="w-24 border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 bg-transparent dark:text-white focus:outline-none text-xs"
                        value={item[f] || ''} onChange={e => update(idx, f, e.target.value || null)} />
                    </td>
                  ))}
                  {(['expected_cartons', 'expected_weight_kg', 'expected_cbm'] as const).map(f => (
                    <td key={f} className="px-1 py-1">
                      <input type="number" step={f === 'expected_cartons' ? '1' : '0.001'} min="0"
                        className="w-14 border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 text-center bg-transparent dark:text-white focus:outline-none text-xs"
                        value={item[f] ?? ''} onChange={e => update(idx, f, e.target.value ? parseFloat(e.target.value) : null)} />
                    </td>
                  ))}
                  {(['delivery_method', 'delivery_tracking_no', 'delivery_address', 'delivery_postcode', 'amazon_ref_id', 'vat_number', 'appointment_code'] as const).map(f => (
                    <td key={f} className="px-1 py-1">
                      <input className="w-24 border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 bg-transparent dark:text-white focus:outline-none text-xs"
                        value={item[f] || ''} onChange={e => update(idx, f, e.target.value || null)} />
                    </td>
                  ))}
                  <td className="px-2 py-1">
                    <button onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="py-8 text-center text-gray-400">No items parsed.</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button onClick={onClose} className={btnSecondary}>Cancel</button>
          <button onClick={() => onConfirm(items)} className={btnPrimary}>Save {items.length} Items</button>
        </div>
      </div>
    </div>
  )
}

// ─── Generate Plan Dialog ────────────────────────────────────────────────────

function GeneratePlanDialog({ ll, items, groups, warehouseId, onDone, onClose }: {
  ll: LoadingList
  items: LoadingListItem[]
  groups: ParsedGroup[]
  warehouseId: string
  onDone: (planIds: string[]) => void
  onClose: () => void
}) {
  const [form, setForm] = useState({ container_no: ll.container_no || '', vessel: '', voyage: '', carrier: '', eta_date: '' })
  const [step, setStep] = useState<'form' | 'review'>('form')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const validIds = useMemo(() => new Set(groups.map(g => g.id)), [groups])
  const groupById = useMemo(() => Object.fromEntries(groups.map(g => [g.id, g.name])), [groups])

  // Full summary including weight and CBM per split
  const summary = useMemo(() => {
    const map = new Map<string, { name: string; count: number; ctns: number; wt: number; cbm: number }>()
    items.forEach(item => {
      const key = (item.group_id && validIds.has(item.group_id)) ? item.group_id : '__none__'
      const name = key === '__none__' ? 'Unassigned' : groupById[key]
      if (!map.has(key)) map.set(key, { name, count: 0, ctns: 0, wt: 0, cbm: 0 })
      const s = map.get(key)!
      s.count++
      s.ctns += item.expected_cartons || 0
      s.wt   += item.expected_weight_kg || 0
      s.cbm  += item.expected_cbm || 0
    })
    return [...map.values()].sort((a, b) => a.name === 'Unassigned' ? 1 : b.name === 'Unassigned' ? -1 : a.name.localeCompare(b.name))
  }, [items, validIds, groupById])

  const totals = useMemo(() => ({
    count: summary.reduce((s, r) => s + r.count, 0),
    ctns:  summary.reduce((s, r) => s + r.ctns,  0),
    wt:    summary.reduce((s, r) => s + r.wt,    0),
    cbm:   summary.reduce((s, r) => s + r.cbm,   0),
  }), [summary])

  const unassigned = summary.find(s => s.name === 'Unassigned')
  const ctnsMatch  = ll.expected_total_cartons != null
    ? totals.ctns === ll.expected_total_cartons : null

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.container_no.trim()) { setError('Container No. is required'); return }
    setError(null)
    setStep('review')
  }

  const handleConfirm = async () => {
    setSaving(true); setError(null)
    try {
      // Upsert by job_ref — never create a duplicate plan for the same job
      const jobRef = ll.job_ref?.trim() || null
      let planId: string

      if (jobRef) {
        const { data: existing } = await supabase
          .from('devanning_plans').select('id').eq('job_ref', jobRef).maybeSingle()
        if (existing) {
          await supabase.from('devanning_plans').update({
            warehouse_id: warehouseId,
            container_no: form.container_no.trim() || null,
            vessel: form.vessel || null, voyage: form.voyage || null,
            carrier: form.carrier || null, eta_date: form.eta_date || null,
            source_loading_list_id: ll.id,
          }).eq('id', existing.id)
          planId = existing.id
        } else {
          const { data: plan, error: planErr } = await supabase.from('devanning_plans')
            .insert({
              job_ref: jobRef,
              warehouse_id: warehouseId,
              container_no: form.container_no.trim() || null,
              vessel: form.vessel || null, voyage: form.voyage || null,
              carrier: form.carrier || null, eta_date: form.eta_date || null,
              source_loading_list_id: ll.id, status: 'pending',
            }).select('id').single()
          if (planErr) throw planErr
          planId = plan.id
        }
      } else {
        const { data: plan, error: planErr } = await supabase.from('devanning_plans')
          .insert({
            warehouse_id: warehouseId,
            container_no: form.container_no.trim(),
            vessel: form.vessel || null, voyage: form.voyage || null,
            carrier: form.carrier || null, eta_date: form.eta_date || null,
            source_loading_list_id: ll.id, status: 'pending',
          }).select('id').single()
        if (planErr) throw planErr
        planId = plan.id
      }

      // Replace plan items (delete existing first to avoid orphans)
      await supabase.from('devanning_plan_items').delete().eq('plan_id', planId)
      if (items.length > 0) {
        const { error: insErr } = await supabase.from('devanning_plan_items').insert(
          items.map((item, i) => ({
            plan_id: planId,
            hbl_no: item.hbl_no || null,
            client_ref: item.job_ref || item.client_ref || null,
            description: item.description || null,
            expected_cartons: item.expected_cartons ?? 0,
            expected_weight_kg: item.expected_weight_kg ?? null,
            expected_cbm: item.expected_cbm ?? null,
            delivery_address: item.delivery_address || null,
            delivery_postcode: item.delivery_postcode || null,
            delivery_contact: item.delivery_contact || null,
            delivery_method: item.delivery_method || null,
            split_no: item.split_no || null,
            system_ref: item.system_ref || null,
            notes: item.notes || null,
            sort_order: i,
            group_name: (item.group_id && validIds.has(item.group_id)) ? (groupById[item.group_id] || null) : null,
          }))
        )
        if (insErr) throw insErr
      }

      // UPSERT devanning (idempotent — safe if already exists)
      const { data: planItemRows } = await supabase
        .from('devanning_plan_items').select('id, sort_order').eq('plan_id', planId).order('sort_order')
      const { data: existingDev } = await supabase
        .from('devannings').select('id').eq('plan_id', planId).maybeSingle()
      let devId: string | null = existingDev?.id ?? null
      if (!devId) {
        const { data: newDev, error: devErr } = await supabase.from('devannings').insert({
          plan_id: planId, warehouse_id: warehouseId,
          container_no: form.container_no.trim() || null, status: 'awaiting',
        }).select('id').single()
        if (devErr) throw devErr
        devId = newDev.id
      }
      if (devId && planItemRows && planItemRows.length > 0) {
        await supabase.from('devanning_items').delete().eq('devanning_id', devId)
        const { error: diErr } = await supabase.from('devanning_items').insert(
          planItemRows.map(pi => {
            const item = items[pi.sort_order] ?? items[0]
            return {
              devanning_id:       devId,
              plan_item_id:       pi.id,
              hbl_no:             item?.hbl_no || null,
              description:        item?.description || null,
              delivery_address:   item?.delivery_address || null,
              delivery_method:    item?.delivery_method || null,
              expected_cartons:   item?.expected_cartons ?? 0,
              expected_weight_kg: item?.expected_weight_kg ?? null,
              expected_cbm:       item?.expected_cbm ?? null,
              actual_cartons: 0, pallet_count: 0, pallets_qty: 0, pallets_detail: [], condition: 'good',
              split_no:   item?.split_no || null,
              system_ref: item?.system_ref || null,
              notes:      item?.notes || null,
              group_name: (item?.group_id && validIds.has(item.group_id)) ? (groupById[item.group_id] || null) : null,
            }
          })
        )
        if (diErr) throw diErr
      }

      onDone([planId])
    } catch (err: any) { setError(err.message || 'Failed'); setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Generate Pre-Devanning Plan</h2>
            <div className="flex gap-2 mt-1">
              {(['form', 'review'] as const).map((s, i) => (
                <span key={s} className={`text-xs px-2 py-0.5 rounded-full font-medium ${step === s ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                  {i + 1}. {s === 'form' ? 'Details' : 'Review'}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Step 1: Form ── */}
        {step === 'form' && (
          <form onSubmit={handleContinue} className="p-6 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Container No. *</label>
              <input className={inputCls} placeholder="e.g. TCKU3456789" value={form.container_no}
                onChange={e => set('container_no', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(['vessel', 'voyage', 'carrier'] as const).map(f => (
                <div key={f}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">{f}</label>
                  <input className={inputCls} value={form[f]} onChange={e => set(f, e.target.value)} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ETA</label>
                <input type="date" className={inputCls} value={form.eta_date} onChange={e => set('eta_date', e.target.value)} />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
              <button type="submit" className={btnPrimary}>Continue to Review →</button>
            </div>
          </form>
        )}

        {/* ── Step 2: Review ── */}
        {step === 'review' && (
          <div className="p-6 space-y-4 overflow-y-auto">

            {/* Form values summary */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-4 py-3 text-sm space-y-1">
              <p className="font-mono font-bold text-gray-900 dark:text-white">{form.container_no}</p>
              <div className="flex flex-wrap gap-x-4 text-gray-500 text-xs">
                {form.vessel   && <span>Vessel: <strong>{form.vessel}</strong></span>}
                {form.voyage   && <span>Voyage: <strong>{form.voyage}</strong></span>}
                {form.carrier  && <span>Carrier: <strong>{form.carrier}</strong></span>}
                {form.eta_date && <span>ETA: <strong>{form.eta_date}</strong></span>}
              </div>
            </div>

            {/* Unassigned warning */}
            {unassigned && unassigned.count > 0 && (
              <div className="flex items-start gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg px-4 py-2.5 text-sm text-yellow-800 dark:text-yellow-300">
                <span className="text-base leading-5">⚠</span>
                <span><strong>Unassigned</strong> has {unassigned.count} item{unassigned.count !== 1 ? 's' : ''} — still proceed?</span>
              </div>
            )}

            {/* Splits summary table */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Devan Split', 'Items', 'CTNS', 'Wt (kg)', 'CBM'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {summary.map(s => (
                    <tr key={s.name} className={s.name === 'Unassigned' ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}>
                      <td className={`px-3 py-2 font-medium ${s.name === 'Unassigned' ? 'text-gray-400 italic' : 'text-gray-800 dark:text-gray-100'}`}>{s.name}</td>
                      <td className="px-3 py-2 text-gray-500">{s.count}</td>
                      <td className="px-3 py-2 font-mono font-semibold">{s.ctns}</td>
                      <td className="px-3 py-2 text-gray-500">{s.wt > 0 ? s.wt.toFixed(1) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{s.cbm > 0 ? s.cbm.toFixed(3) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold text-sm">
                  <tr>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">Total</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{totals.count}</td>
                    <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{totals.ctns}</td>
                    <td className="px-3 py-2 text-gray-500">{totals.wt > 0 ? totals.wt.toFixed(1) : '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{totals.cbm > 0 ? totals.cbm.toFixed(3) : '—'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>


            {/* Per-item preview */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">All Items ({items.length})</p>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto text-xs">
                <table className="min-w-max divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {['HBL No.', 'Group', 'Description', 'Address', 'Postcode', 'Contact', 'Method', 'CTNS', 'Wt', 'CBM'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {items.map((it, i) => {
                      const grpName = (it.group_id && validIds.has(it.group_id)) ? groupById[it.group_id] : null
                      const badCtns = it.expected_cartons != null && !Number.isInteger(it.expected_cartons)
                      return (
                        <tr key={i} className={badCtns ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                          <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">{it.hbl_no || '—'}</td>
                          <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{grpName || <span className="italic text-gray-300">none</span>}</td>
                          <td className="px-2 py-1 text-gray-700 dark:text-gray-300 max-w-[180px] truncate">{it.description || '—'}</td>
                          <td className="px-2 py-1 text-gray-500 max-w-[140px] truncate">{it.delivery_address || '—'}</td>
                          <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{it.delivery_postcode || '—'}</td>
                          <td className="px-2 py-1 text-gray-500 max-w-[100px] truncate">{it.delivery_contact || '—'}</td>
                          <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{it.delivery_method || '—'}</td>
                          <td className={`px-2 py-1 font-mono text-right whitespace-nowrap ${badCtns ? 'text-red-600 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>{it.expected_cartons ?? '—'}</td>
                          <td className="px-2 py-1 text-gray-500 text-right whitespace-nowrap">{it.expected_weight_kg != null ? it.expected_weight_kg.toFixed(1) : '—'}</td>
                          <td className="px-2 py-1 text-gray-500 text-right whitespace-nowrap">{it.expected_cbm != null ? it.expected_cbm.toFixed(3) : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expected vs total comparison */}
            {ll.expected_total_cartons != null && (
              <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm ${
                ctnsMatch
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                  : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300'
              }`}>
                {ctnsMatch ? (
                  <><span>✓</span><span>Matches expected — <strong>{ll.expected_total_cartons}</strong> ctns</span></>
                ) : (
                  <><span>⚠</span><span>
                    Expected: <strong>{ll.expected_total_cartons}</strong> ctns &nbsp;/&nbsp;
                    Total: <strong>{totals.ctns}</strong> ctns &nbsp;/&nbsp;
                    Difference: <strong>{totals.ctns - ll.expected_total_cartons > 0 ? '+' : ''}{totals.ctns - ll.expected_total_cartons}</strong>
                  </span></>
                )}
              </div>
            )}

            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex justify-between gap-3 pt-1">
              <button onClick={() => setStep('form')} className={btnSecondary}>← Back</button>
              <div className="flex gap-3">
                <button onClick={onClose} className={btnSecondary}>Cancel</button>
                <button onClick={handleConfirm} disabled={saving} className={btnPrimary}>
                  {saving ? 'Creating…' : 'Confirm & Generate Plan'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function LoadingListDetail() {
  const { id, lid } = useParams<{ id: string; lid: string }>()
  const navigate = useNavigate()
  const { userEmail } = useAuth()
  const { toast } = useToast()
  const [ll, setLl] = useState<LoadingList | null>(null)
  const [items, setItems] = useState<LoadingListItem[]>([])
  const [groups, setGroups] = useState<ParsedGroup[]>([])
  const [saving, setSaving] = useState(false)
  const [showGenerate, setShowGenerate] = useState(false)
  const [linkedPlan, setLinkedPlan] = useState<{ id: string; itemCount: number } | null | undefined>(undefined)
  const [syncingPlan, setSyncingPlan] = useState(false)
  const [addRow, setAddRow] = useState<LoadingListItem>({ ...EMPTY_ITEM })
  const [addingRow, setAddingRow] = useState(false)

  // Selection & grouping
  const [selectedSet, setSelectedSet] = useState<Set<number>>(new Set())
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renamingVal, setRenamingVal] = useState('')
  const [moveTarget, setMoveTarget] = useState('')

  // Sort
  const [sortCol, setSortCol] = useState<keyof LoadingListItem>('delivery_address')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Expected totals (cartons, weight, CBM)
  const [ctnsTargetEdit, setCtnsTargetEdit] = useState(false)
  const [ctnsTargetInput, setCtnsTargetInput] = useState('')
  const [wtTargetInput, setWtTargetInput] = useState('')
  const [cbmTargetInput, setCbmTargetInput] = useState('')

  // Container No. inline edit
  const [containerEdit, setContainerEdit] = useState(false)
  const [containerInput, setContainerInput] = useState('')

  // Free demurrage
  const [etaEdit, setEtaEdit] = useState(false)
  const [etaInput, setEtaInput] = useState('')
  const [freeDaysInput, setFreeDaysInput] = useState('5')

  // Column visibility (persisted in localStorage + DB user_preferences)
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('ll_hidden_cols')
      return saved ? new Set(JSON.parse(saved)) : new Set(DEFAULT_HIDDEN_COLS)
    } catch { return new Set(DEFAULT_HIDDEN_COLS) }
  })
  const [showColPicker, setShowColPicker] = useState(false)

  // Column order (persisted in localStorage + DB user_preferences)
  const [colOrder, setColOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('ll_col_order')
      if (saved) {
        const parsed = JSON.parse(saved) as string[]
        const allKeys = ALL_COL_DEFS.map(c => c.key)
        const merged = parsed.filter(k => allKeys.includes(k))
        const extra = allKeys.filter(k => !merged.includes(k))
        return [...merged, ...extra]
      }
    } catch { /* ignore */ }
    return ALL_COL_DEFS.map(c => c.key)
  })

  // Load column prefs from DB on mount (overrides localStorage for cross-device sync)
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from('user_preferences').select('preferences').eq('user_id', user.id).maybeSingle()
        .then(({ data }) => {
          const p = data?.preferences?.ll_detail_cols
          if (!p) return
          if (Array.isArray(p.hidden)) setHiddenCols(new Set(p.hidden))
          if (Array.isArray(p.order)) {
            const allKeys = ALL_COL_DEFS.map(c => c.key)
            const merged = (p.order as string[]).filter(k => allKeys.includes(k))
            const extra = allKeys.filter(k => !merged.includes(k))
            setColOrder([...merged, ...extra])
          }
          if (p.sort?.col) setSortCol(p.sort.col as keyof LoadingListItem)
          if (p.sort?.dir) setSortDir(p.sort.dir as 'asc' | 'desc')
        })
    })
  }, [])
  const dragColKey = useRef<string | null>(null)

  // 3-dot menu
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  // AI parse
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [parsePreview, setParsePreview] = useState<ParseResult | null>(null)

  // Column mapping flow
  const [rawExtract, setRawExtract] = useState<{ columns: RawColumn[]; rows: any[][]; previewRows: any[][]; headerRowIdx: number } | null>(null)
  const [savedMappingCustomerName, setSavedMappingCustomerName] = useState<string | null>(null)
  const [showSmartSplit, setShowSmartSplit] = useState(false)
  const [splitConfig, setSplitConfig] = useState<SplitConfig | null>(null)

  // ── Data loading ─────────────────────────────────────────────────────────────

  const fetchLL = useCallback(async () => {
    if (!lid) return
    const { data } = await supabase.from('loading_lists')
      .select('*, customer:customers(code, name)')
      .eq('id', lid).single()
    if (data) {
      setLl(data as LoadingList)
      setItems((data.parsed_data?.items || []).map((it: LoadingListItem) => ({ ...it, group_id: it.group_id ?? null })))
      setGroups(data.parsed_data?.groups || [])
      setCtnsTargetInput(data.expected_total_cartons != null ? String(data.expected_total_cartons) : '')
      setWtTargetInput(data.expected_total_weight_kg != null ? String(data.expected_total_weight_kg) : '')
      setCbmTargetInput(data.expected_total_cbm != null ? String(data.expected_total_cbm) : '')
      setEtaInput(data.container_eta_at ? new Date(data.container_eta_at).toISOString().slice(0, 10) : '')
      setFreeDaysInput(data.free_demurrage_days != null ? String(data.free_demurrage_days) : '5')
      setContainerInput(data.container_no || '')
      // Check for existing devanning plan by job_ref
      const jobRef = (data as any).job_ref?.trim() || null
      if (jobRef) {
        supabase.from('devanning_plans').select('id').eq('job_ref', jobRef).maybeSingle()
          .then(({ data: plan }) => {
            if (plan) {
              supabase.from('devanning_plan_items').select('id', { count: 'exact', head: true }).eq('plan_id', plan.id)
                .then(({ count }) => setLinkedPlan({ id: plan.id, itemCount: count ?? 0 }))
            } else {
              setLinkedPlan(null)
            }
          })
      } else {
        setLinkedPlan(null)
      }
    }
  }, [lid])

  useEffect(() => { fetchLL() }, [fetchLL])

  // ── Persist ───────────────────────────────────────────────────────────────────

  // Cache of linked plan items to avoid re-querying on every save.
  // Shape: [{ planId, items: [{ id, hbl_no, sort_order }] }]
  const linkedPlanCache = useRef<{ planId: string; items: { id: string; hbl_no: string | null; sort_order: number }[] }[]>([])
  const linkedPlanCacheLoaded = useRef(false)
  const syncDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Full replace of plan_items for every non-cancelled plan linked to this LL.
  // Used after Confirm Mapping and after Smart Split so the plan always reflects
  // the current LL items and group assignments. Safe: devanning_items.plan_item_id
  // FK is ON DELETE SET NULL, so existing devanning work survives.
  const replaceLinkedPlanItems = useCallback(async (llItems: LoadingListItem[], llGroups: ParsedGroup[]) => {
    if (!lid || llItems.length === 0) return
    const validGrpIds = new Set(llGroups.map(g => g.id))
    const grpById = Object.fromEntries(llGroups.map(g => [g.id, g.name]))

    const { data: plans } = await supabase
      .from('devanning_plans')
      .select('id')
      .eq('source_loading_list_id', lid)
      .neq('status', 'cancelled')
    if (!plans || plans.length === 0) return

    for (const plan of plans) {
      await supabase.from('devanning_plan_items').delete().eq('plan_id', plan.id)
      const { error: insErr } = await supabase.from('devanning_plan_items').insert(
        llItems.map((item, i) => ({
          plan_id: plan.id,
          hbl_no:             item.hbl_no             || null,
          client_ref:         item.job_ref || item.client_ref || null,
          description:        item.description        || null,
          expected_cartons:   item.expected_cartons   ?? 0,
          expected_weight_kg: item.expected_weight_kg ?? null,
          expected_cbm:       item.expected_cbm       ?? null,
          delivery_address:   item.delivery_address   || null,
          delivery_postcode:  item.delivery_postcode  || null,
          delivery_contact:   item.delivery_contact   || null,
          delivery_method:    item.delivery_method    || null,
          split_no:           item.split_no           || null,
          system_ref:         item.system_ref         || null,
          notes:              item.notes              || null,
          sort_order:         i,
          group_name:         (item.group_id && validGrpIds.has(item.group_id))
                                ? (grpById[item.group_id] || null) : null,
        }))
      )
      if (insErr) throw insErr
    }
    linkedPlanCacheLoaded.current = false  // force re-query on next syncToLinkedPlans
  }, [lid])

  const syncToPlanInFlight = useRef(false)

  const handleSyncToPlan = useCallback(async () => {
    if (!linkedPlan || !lid) return
    // Guard must be a synchronous ref, not the syncingPlan state — window.confirm()
    // blocks the JS thread, so a double-click queues a second invocation that fires
    // as soon as the first confirm() resolves, before React re-renders to disable
    // the button. That let two full delete+insert cycles interleave and double the
    // item count (120 -> 240). Checking/setting this ref before confirm() closes it.
    if (syncToPlanInFlight.current) return
    syncToPlanInFlight.current = true
    try {
      if (linkedPlan.itemCount > 0) {
        if (!window.confirm(`Plan already has ${linkedPlan.itemCount} item${linkedPlan.itemCount !== 1 ? 's' : ''}. Replace all with latest LL data?`)) return
      }
      setSyncingPlan(true)
      const validGrpIds = new Set(groups.map(g => g.id))
      const grpById = Object.fromEntries(groups.map(g => [g.id, g.name]))
      const { error: delErr } = await supabase.from('devanning_plan_items').delete().eq('plan_id', linkedPlan.id)
      if (delErr) throw delErr
      if (items.length > 0) {
        const { error: insErr } = await supabase.from('devanning_plan_items').insert(
          items.map((item, i) => ({
            plan_id:            linkedPlan.id,
            hbl_no:             item.hbl_no             || null,
            client_ref:         item.job_ref || item.client_ref || null,
            description:        item.description        || null,
            expected_cartons:   item.expected_cartons   ?? 0,
            expected_weight_kg: item.expected_weight_kg ?? null,
            expected_cbm:       item.expected_cbm       ?? null,
            delivery_address:   item.delivery_address   || null,
            delivery_postcode:  item.delivery_postcode  || null,
            delivery_contact:   item.delivery_contact   || null,
            delivery_method:    item.delivery_method    || null,
            split_no:           item.split_no           || null,
            system_ref:         item.system_ref         || null,
            notes:              item.notes              || null,
            sort_order:         i,
            group_name:         (item.group_id && validGrpIds.has(item.group_id)) ? (grpById[item.group_id] || null) : null,
          }))
        )
        if (insErr) throw insErr
      }
      await supabase.from('devanning_plans').update({ source_loading_list_id: lid }).eq('id', linkedPlan.id)

      // Sync devanning_items (idempotent — UPSERT devanning then replace items)
      const { data: planItemRows } = await supabase
        .from('devanning_plan_items').select('id, sort_order').eq('plan_id', linkedPlan.id).order('sort_order')
      const { data: existingDev } = await supabase
        .from('devannings').select('id').eq('plan_id', linkedPlan.id).maybeSingle()
      let devId: string | null = existingDev?.id ?? null
      if (!devId) {
        const { data: newDev, error: devErr } = await supabase.from('devannings').insert({
          plan_id: linkedPlan.id, warehouse_id: id!,
          container_no: ll?.container_no || null, status: 'awaiting',
        }).select('id').single()
        if (devErr) throw devErr
        devId = newDev.id
      }
      if (devId && planItemRows && planItemRows.length > 0) {
        const { error: delDiErr } = await supabase.from('devanning_items').delete().eq('devanning_id', devId)
        if (delDiErr) throw delDiErr
        const { error: diErr } = await supabase.from('devanning_items').insert(
          planItemRows.map(pi => {
            const item = items[pi.sort_order] ?? items[0]
            return {
              devanning_id: devId, plan_item_id: pi.id,
              hbl_no:             item?.hbl_no || null,
              description:        item?.description || null,
              delivery_address:   item?.delivery_address || null,
              delivery_method:    item?.delivery_method || null,
              expected_cartons:   item?.expected_cartons ?? 0,
              expected_weight_kg: item?.expected_weight_kg ?? null,
              expected_cbm:       item?.expected_cbm ?? null,
              actual_cartons: 0, pallet_count: 0, pallets_qty: 0, pallets_detail: [], condition: 'good',
              split_no:   item?.split_no || null,
              system_ref: item?.system_ref || null,
              notes:      item?.notes || null,
              group_name: (item?.group_id && validGrpIds.has(item.group_id)) ? (grpById[item.group_id] || null) : null,
            }
          })
        )
        if (diErr) throw diErr
      }

      navigate(`/warehouse/${id}/plans/${linkedPlan.id}`)
    } catch (e: any) {
      alert(`Sync failed: ${e.message}`)
      setSyncingPlan(false)
    } finally {
      syncToPlanInFlight.current = false
    }
  }, [linkedPlan, lid, items, groups, id, ll, navigate])

  const syncToLinkedPlans = useCallback(async (llItems: LoadingListItem[]) => {
    if (!lid) return

    if (!linkedPlanCacheLoaded.current) {
      const { data: plans } = await supabase
        .from('devanning_plans')
        .select('id, devanning_plan_items(id, hbl_no, sort_order)')
        .eq('source_loading_list_id', lid)
        .neq('status', 'cancelled')
      linkedPlanCache.current = ((plans ?? []) as any[]).map(p => ({
        planId: p.id,
        items: Array.isArray(p.devanning_plan_items) ? p.devanning_plan_items : [],
      }))
      linkedPlanCacheLoaded.current = true
    }

    for (const plan of linkedPlanCache.current) {
      const byHbl = new Map<string, string>()
      const byIdx = new Map<number, string>()
      for (const pi of plan.items) {
        if (pi.hbl_no) byHbl.set(pi.hbl_no, pi.id)
        byIdx.set(pi.sort_order, pi.id)
      }

      const updates: { id: string; patch: Record<string, unknown> }[] = []
      for (let i = 0; i < llItems.length; i++) {
        const src = llItems[i]
        const planItemId = (src.hbl_no && byHbl.has(src.hbl_no))
          ? byHbl.get(src.hbl_no)!
          : byIdx.get(i) ?? null
        if (!planItemId) continue
        updates.push({
          id: planItemId,
          patch: {
            hbl_no:             src.hbl_no             ?? null,
            client_ref:         src.job_ref || src.client_ref || null,
            description:        src.description        ?? null,
            expected_cartons:   src.expected_cartons   ?? 0,
            expected_weight_kg: src.expected_weight_kg ?? null,
            expected_cbm:       src.expected_cbm       ?? null,
            delivery_address:   src.delivery_address   ?? null,
            delivery_postcode:  src.delivery_postcode  ?? null,
            delivery_contact:   src.delivery_contact   ?? null,
            delivery_method:    src.delivery_method    ?? null,
            split_no:           src.split_no           ?? null,
            system_ref:         src.system_ref         ?? null,
          },
        })
      }

      await Promise.all(updates.map(({ id, patch }) =>
        supabase.from('devanning_plan_items').update(patch).eq('id', id)
      ))
    }
  }, [lid])

  const saveAll = useCallback(async (newItems: LoadingListItem[], newGroups: ParsedGroup[]) => {
    setSaving(true)
    await supabase.from('loading_lists')
      .update({ parsed_data: { items: newItems, groups: newGroups } })
      .eq('id', lid!)
    setSaving(false)
    // Debounced auto-sync to any linked pre-devan plan items (1.5 s after last change)
    if (syncDebounce.current) clearTimeout(syncDebounce.current)
    syncDebounce.current = setTimeout(() => syncToLinkedPlans(newItems).catch(console.error), 1500)
  }, [lid, syncToLinkedPlans])

  // ── Item edits ────────────────────────────────────────────────────────────────

  const updateItem = (idx: number, k: keyof LoadingListItem, v: unknown) => {
    setItems(prev => {
      const next = prev.map((it, i) => i === idx ? { ...it, [k]: v } : it)
      saveAll(next, groups)
      return next
    })
  }

  const deleteItem = (idx: number) => {
    setSelectedSet(prev => { const n = new Set(prev); n.delete(idx); return n })
    setItems(prev => {
      const next = prev.filter((_, i) => i !== idx)
      saveAll(next, groups)
      return next
    })
  }

  const handleAddRow = async () => {
    if (!addRow.description.trim()) return
    setAddingRow(true)
    const next = [...items, { ...addRow, group_id: null }]
    setItems(next)
    await saveAll(next, groups)
    setAddRow({ ...EMPTY_ITEM })
    setAddingRow(false)
  }

  // ── Group operations ──────────────────────────────────────────────────────────

  const addToNewGroup = () => {
    if (selectedSet.size === 0) return
    const n = groups.length + 1
    const newGroup: ParsedGroup = { id: `grp_${Date.now()}`, name: `Devan Split ${n}`, order: n - 1 }
    const newGroups = [...groups, newGroup]
    const newItems = items.map((it, idx) => selectedSet.has(idx) ? { ...it, group_id: newGroup.id } : it)
    setGroups(newGroups); setItems(newItems); setSelectedSet(new Set()); setMoveTarget('')
    saveAll(newItems, newGroups)
    setRenamingId(newGroup.id); setRenamingVal(newGroup.name)
  }

  const moveToGroup = (targetId: string) => {
    if (selectedSet.size === 0) return
    const gid = targetId === '__unassigned__' ? null : targetId
    const newItems = items.map((it, idx) => selectedSet.has(idx) ? { ...it, group_id: gid } : it)
    setItems(newItems); setSelectedSet(new Set()); setMoveTarget('')
    saveAll(newItems, groups)
  }

  const deleteGroup = (groupId: string) => {
    const newGroups = groups.filter(g => g.id !== groupId)
    const newItems = items.map(it => it.group_id === groupId ? { ...it, group_id: null } : it)
    setGroups(newGroups); setItems(newItems)
    saveAll(newItems, newGroups)
  }

  const commitRename = () => {
    if (!renamingId) return
    const name = renamingVal.trim() || 'Devan Split'
    const newGroups = groups.map(g => g.id === renamingId ? { ...g, name } : g)
    setGroups(newGroups); setRenamingId(null)
    saveAll(items, newGroups)
  }

  // ── Selection helpers ─────────────────────────────────────────────────────────

  const toggleItem = (idx: number) =>
    setSelectedSet(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n })

  const toggleGroupSelect = (indices: number[]) => {
    const allIn = indices.every(i => selectedSet.has(i))
    setSelectedSet(prev => {
      const n = new Set(prev)
      allIn ? indices.forEach(i => n.delete(i)) : indices.forEach(i => n.add(i))
      return n
    })
  }

  const toggleSelectAll = () => {
    if (selectedSet.size === items.length && items.length > 0) setSelectedSet(new Set())
    else setSelectedSet(new Set(items.map((_, i) => i)))
  }

  // ── Sort ──────────────────────────────────────────────────────────────────────

  const handleSortClick = (col: keyof LoadingListItem) => {
    const newDir: 'asc' | 'desc' = sortCol === col ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
    setSortCol(col)
    setSortDir(newDir)
    savePrefsToDb([...hiddenCols], colOrder, col, newDir)
  }

  // ── Sections ──────────────────────────────────────────────────────────────────

  const sections = useMemo(() => {
    const validIds = new Set(groups.map(g => g.id))
    const unassignedIdx: number[] = []
    const groupedIdx = new Map<string, number[]>()
    items.forEach((it, idx) => {
      const gid = it.group_id
      if (!gid || !validIds.has(gid)) unassignedIdx.push(idx)
      else { if (!groupedIdx.has(gid)) groupedIdx.set(gid, []); groupedIdx.get(gid)!.push(idx) }
    })
    const sortByField = (indices: number[]) => [...indices].sort((a, b) => {
      const va = items[a][sortCol], vb = items[b][sortCol]
      if (va == null && vb == null) return 0
      if (va == null) return sortDir === 'asc' ? 1 : -1
      if (vb == null) return sortDir === 'asc' ? -1 : 1
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    })
    return [
      { id: null as string | null, name: 'Unassigned', indices: sortByField(unassignedIdx) },
      ...[...groups].sort((a, b) => a.order - b.order)
        .map(g => ({ id: g.id, name: g.name, indices: sortByField(groupedIdx.get(g.id) || []) })),
    ]
  }, [items, groups, sortCol, sortDir])

  // ── Derived ───────────────────────────────────────────────────────────────────

  const itemsCtnsTotal = useMemo(() => items.reduce((s, it) => s + (it.expected_cartons || 0), 0), [items])

  const visibleCols = useMemo(
    () => colOrder.map(k => ALL_COL_DEFS.find(c => c.key === k)!).filter(c => c && !hiddenCols.has(c.key)),
    [hiddenCols, colOrder]
  )
  const totalCols = visibleCols.length + 3  // checkbox + # + delete

  // B3/Z50.3: same mirrored-fixed-scrollbar pattern as Z34's fix for the Jobs list, extracted
  // to a shared hook/component so both tables (and future warehouse tables) can reuse it.
  const { scrollProps: itemsScrollProps, contentRef: itemsTableRef, bar: itemsScrollBar } = useStickyHScroll([items, visibleCols])

  const savePrefsToDb = async (hidden: string[], order: string[], sc: string, sd: string) => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    supabase.from('user_preferences').upsert({
      user_id: user.id,
      preferences: { ll_detail_cols: { hidden, order, sort: { col: sc, dir: sd } } },
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' })
  }

  const toggleCol = (key: string) => {
    const next = new Set(hiddenCols)
    next.has(key) ? next.delete(key) : next.add(key)
    setHiddenCols(next)
    const arr = [...next]
    localStorage.setItem('ll_hidden_cols', JSON.stringify(arr))
    savePrefsToDb(arr, colOrder, sortCol, sortDir)
  }

  const handleColDragStart = (key: string) => { dragColKey.current = key }
  const handleColDragOver = (e: React.DragEvent) => e.preventDefault()
  const handleColDrop = (targetKey: string) => {
    const from = dragColKey.current
    if (!from || from === targetKey) return
    dragColKey.current = null
    const next = [...colOrder]
    const fromIdx = next.indexOf(from)
    const toIdx = next.indexOf(targetKey)
    next.splice(fromIdx, 1)
    next.splice(toIdx, 0, from)
    setColOrder(next)
    localStorage.setItem('ll_col_order', JSON.stringify(next))
    savePrefsToDb([...hiddenCols], next, sortCol, sortDir)
  }

  // ── Smart Devan Split ─────────────────────────────────────────────────────────

  const handleApplySmartSplit = (config: SplitConfig) => {
    const { newItems, newGroups } = applySmartSplit(items, config.criteria)
    setSplitConfig(config)
    setGroups(newGroups)
    setItems(newItems)
    saveAll(newItems, newGroups)
    setShowSmartSplit(false)
    // Replace plan_items with split group assignments so plan detail shows splits immediately
    replaceLinkedPlanItems(newItems, newGroups).catch(console.error)

    // Save split config to customer history
    if (ll?.source_customer_id && ll?.column_mapping) {
      const sig = (ll.raw_columns || []).map((c: RawColumn) => c.header).sort().join('|')
      if (sig) {
        supabase.from('customer_column_mapping_history').upsert({
          customer_id: ll.source_customer_id,
          file_signature: sig,
          mapping: ll.column_mapping,
          split_config: config,
          last_used_at: new Date().toISOString(),
        }, { onConflict: 'customer_id,file_signature' }).then(({ error }) => { if (error) console.error(error) })
      }
    }
  }

  // ── Cartons target ────────────────────────────────────────────────────────────

  const handleSetCtnsTarget = async () => {
    const ctnsRaw = ctnsTargetInput.trim()
    const wtRaw   = wtTargetInput.trim()
    const cbmRaw  = cbmTargetInput.trim()
    const ctnsVal = ctnsRaw === '' ? null : Math.round(parseFloat(ctnsRaw))
    const wtVal   = wtRaw   === '' ? null : parseFloat(wtRaw)
    const cbmVal  = cbmRaw  === '' ? null : parseFloat(cbmRaw)
    if (ctnsVal !== null && isNaN(ctnsVal)) return
    if (wtVal   !== null && isNaN(wtVal))   return
    if (cbmVal  !== null && isNaN(cbmVal))  return
    setSaving(true)
    await supabase.from('loading_lists').update({
      expected_total_cartons:   ctnsVal,
      expected_total_weight_kg: wtVal,
      expected_total_cbm:       cbmVal,
    }).eq('id', lid!)
    setLl(prev => prev ? { ...prev, expected_total_cartons: ctnsVal, expected_total_weight_kg: wtVal, expected_total_cbm: cbmVal } : prev)
    setCtnsTargetEdit(false)
    setSaving(false)
  }

  // ── ETA / Free Demurrage ─────────────────────────────────────────────────────

  const handleSetEta = async () => {
    const etaVal = etaInput ? new Date(etaInput).toISOString() : null
    const daysVal = freeDaysInput ? parseInt(freeDaysInput) : 5
    setSaving(true)
    await supabase.from('loading_lists').update({
      container_eta_at: etaVal,
      free_demurrage_days: daysVal,
    }).eq('id', lid!)
    setLl(prev => prev ? { ...prev, container_eta_at: etaVal, free_demurrage_days: daysVal } : prev)
    setEtaEdit(false)
    setSaving(false)
  }

  // ── Container No. edit ───────────────────────────────────────────────────────

  const handleSetContainer = async () => {
    const val = containerInput.trim() || null
    setSaving(true)
    await supabase.from('loading_lists').update({ container_no: val }).eq('id', lid!)
    setLl(prev => prev ? { ...prev, container_no: val } : prev)
    setContainerEdit(false)
    setSaving(false)
  }

  // ── Excel reader (browser-side — avoids large round-trips to edge fn) ──────────

  const readXlsxRows = async (fileUrl: string): Promise<{ rows: any[][]; sheetName: string }> => {
    const resp = await fetch(fileUrl)
    if (!resp.ok) throw new Error(`Failed to download file (${resp.status})`)
    const arrayBuffer = await resp.arrayBuffer()
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true })
    const sheetName = workbook.SheetNames.find((n: string) =>
      n === 'LIST' || n === 'Sheet1' || n.includes('打板') || n.includes('明细')
    ) || workbook.SheetNames[0]
    const ws = workbook.Sheets[sheetName]
    for (const m of (ws['!merges'] || [])) {
      const masterAddr = XLSX.utils.encode_cell({ r: m.s.r, c: m.s.c })
      const masterCell = ws[masterAddr]
      if (!masterCell) continue
      for (let r = m.s.r; r <= m.e.r; r++) {
        for (let c = m.s.c; c <= m.e.c; c++) {
          const addr = XLSX.utils.encode_cell({ r, c })
          if (!ws[addr]) ws[addr] = { ...masterCell }
        }
      }
    }
    return { rows: XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][], sheetName }
  }

  // ── AI Parse (Phase 1: dumb extraction) ──────────────────────────────────────

  const handleParseWithAI = async () => {
    if (!ll?.file_url) { setParseError('No file attached.'); return }
    setParsing(true); setParseError(null)
    try {
      // Parse Excel in browser — reliable, no Deno/npm dependency required
      const { rows, sheetName } = await readXlsxRows(ll.file_url)

      // Phase 1: edge function extracts column metadata from rows
      const { data, error } = await supabase.functions.invoke('parse-loading-list', {
        body: { rows, sheet_name: sheetName, filename: ll.file_name || '' },
      })
      if (error) throw new Error(error.message || 'Edge function error')
      const { columns, header_row_idx } = data as { columns: RawColumn[]; header_row_idx: number }
      if (!columns || !Array.isArray(columns)) throw new Error('No columns returned from parse')

      // Save raw_columns to DB (frontend does this; edge fn need not touch DB)
      await supabase.from('loading_lists').update({ raw_columns: columns }).eq('id', lid!)
      setLl(prev => prev ? { ...prev, raw_columns: columns } : prev)

      // Load saved mapping from customer history (if any)
      let initialMapping: Record<number, MappingTarget> | undefined
      setSavedMappingCustomerName(null)
      if (ll.source_customer_id) {
        const sig = columns.map((c: RawColumn) => c.header).sort().join('|')
        const { data: hist } = await supabase
          .from('customer_column_mapping_history')
          .select('mapping')
          .eq('customer_id', ll.source_customer_id)
          .eq('file_signature', sig)
          .maybeSingle()
        if (hist?.mapping) {
          initialMapping = hist.mapping as Record<number, MappingTarget>
        } else {
          // Fall back to per-customer last-used mapping (any file layout)
          const { data: saved } = await supabase
            .from('customer_column_mappings')
            .select('mapping_json')
            .eq('customer_id', ll.source_customer_id)
            .maybeSingle()
          if (saved?.mapping_json) {
            initialMapping = saved.mapping_json as Record<number, MappingTarget>
            setSavedMappingCustomerName(ll.customer?.name ?? ll.source_customer_id)
          }
        }
      }

      if (!initialMapping) {
        const suggestions = suggestMapping(columns)
        initialMapping = Object.fromEntries(
          Object.entries(suggestions).map(([k, v]) => [parseInt(k), v.target])
        ) as Record<number, MappingTarget>
      }

      // Store full rows for phase 2; previewRows (header + 10) for mapping UI display
      const previewRows = rows.slice(0, header_row_idx + 11)
      setRawExtract({ columns, rows, previewRows, headerRowIdx: header_row_idx })
      setLl(prev => prev ? { ...prev, column_mapping: initialMapping as any } : prev)
    } catch (err: any) {
      setParseError(err.message || 'Unknown error during parse')
    } finally {
      setParsing(false)
    }
  }

  // ── Phase 2: user confirms mapping → generate items via edge fn ───────────────

  const handleConfirmMapping = async (mapping: Record<number, MappingTarget>) => {
    if (!rawExtract || !ll) return
    setParsing(true); setParseError(null)
    try {
      await supabase.from('loading_lists').update({ column_mapping: mapping }).eq('id', lid!)

      if (ll.source_customer_id) {
        const sig = rawExtract.columns.map(c => c.header).sort().join('|')
        const { error: histErr } = await supabase.from('customer_column_mapping_history').upsert({
          customer_id: ll.source_customer_id,
          file_signature: sig,
          mapping,
          last_used_at: new Date().toISOString(),
        }, { onConflict: 'customer_id,file_signature' })
        if (histErr) console.error('[#236] customer_column_mapping_history upsert failed:', histErr)

        const { error: ccmErr } = await supabase.from('customer_column_mappings').upsert({
          customer_id: ll.source_customer_id,
          mapping_json: mapping,
          updated_at: new Date().toISOString(),
          updated_by: userEmail,
        }, { onConflict: 'customer_id' })
        if (ccmErr) console.error('[#236] customer_column_mappings upsert failed:', ccmErr)
        else console.log('[#236] customer_column_mappings upserted for', ll.source_customer_id)

        setSavedMappingCustomerName(null)
      } else {
        console.warn('[#236] handleConfirmMapping: ll.source_customer_id is null, skipping upsert')
      }

      // Phase 2: generate items from confirmed mapping (rows already in memory).
      // Timeout-guarded: the edge fn calls out to the AI cleanup step, which can
      // stall on a slow/hung upstream request and otherwise leave the dialog
      // spinning on "Processing…" forever with no client-side backstop.
      const CONFIRM_TIMEOUT_MS = 30_000
      const invokePromise = supabase.functions.invoke('parse-loading-list', {
        body: {
          rows: rawExtract.rows,
          header_row_idx: rawExtract.headerRowIdx,
          mapping: Object.fromEntries(Object.entries(mapping).map(([k, v]) => [k, v])),
          filename: ll.file_name || '',
        },
      })
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Processing took too long, please retry')), CONFIRM_TIMEOUT_MS)
      })
      const { data, error } = await Promise.race([invokePromise, timeoutPromise])
      if (error) throw new Error(error.message || 'Generate error')
      const { items: parsedItems, skipped_rows } = data as { items: LoadingListItem[]; skipped_rows: number }
      if (!parsedItems) throw new Error('No items returned')

      setRawExtract(null)
      setParsePreview({ items: parsedItems, format_detected: 'mapped', skipped_rows })
    } catch (err: any) {
      const message = err?.message || 'Unknown error'
      // The mapping dialog is a full-screen overlay — the inline banner below
      // renders underneath it, so surface a toast too or the user sees nothing.
      toast(message, 'error')
      setParseError(message)
    } finally {
      setParsing(false)
    }
  }

  const handleConfirmParse = async (parsedItems: LoadingListItem[]) => {
    const withGroup = parsedItems.map(it => ({ ...it, group_id: null as string | null }))
    setSaving(true)
    await supabase.from('loading_lists').update({
      parsed_data: { items: withGroup, groups: [] },
      parse_status: 'parsed',
      mapping_confirmed: true,
      mapping_confirmed_at: new Date().toISOString(),
      mapping_confirmed_by: userEmail,
    }).eq('id', lid!)
    setItems(withGroup); setGroups([])
    setLl(prev => prev ? { ...prev, parse_status: 'parsed', parsed_data: { items: withGroup, groups: [] } } : prev)
    setParsePreview(null)
    // Push items to any linked pre-devanning plan (e.g. plan created from Schedule with 0 items)
    await replaceLinkedPlanItems(withGroup, [])
    setSaving(false)
  }

  // Auto-parse when file exists but not yet parsed
  useEffect(() => {
    if (ll?.file_url && !ll.parsed_data && ll.parse_status !== 'parsed') {
      handleParseWithAI()
    }
  }, [ll?.id])

  // ── Render ────────────────────────────────────────────────────────────────────

  if (!ll) return <WarehouseLayout warehouseId={id!}><div className="py-16 text-center text-gray-400">Loading...</div></WarehouseLayout>

  const allSelected = items.length > 0 && selectedSet.size === items.length
  const someSelected = selectedSet.size > 0 && !allSelected
  const cellCls = 'w-full min-w-[56px] border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 text-xs bg-transparent dark:text-white focus:outline-none'
  const wrapCellCls = 'w-full min-w-[120px] border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 text-xs bg-transparent dark:text-white focus:outline-none resize-none leading-snug break-words'
  // Reset to 0 first so scrollHeight reflects full content, not clamped visible height
  const autoHeight = (el: HTMLTextAreaElement | null) => { if (el) { el.style.height = '0'; el.style.height = el.scrollHeight + 'px' } }
  const wrapStyle: React.CSSProperties = { fieldSizing: 'content' as any, minHeight: '1.25rem', maxHeight: '8rem', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }

  const ctnsTarget = ll.expected_total_cartons
  const ctnsDelta = ctnsTarget != null ? itemsCtnsTotal - ctnsTarget : null

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">

        {/* ── Page header ── */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Link to={`/warehouse/${id}/loading-lists`} className="text-sm text-blue-600 hover:underline block mb-1">← Loading Lists</Link>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{ll.file_name || 'Manual Entry'}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1 items-center">
              {ll.job_ref && <span>Job Ref: <strong className="font-mono text-blue-700 dark:text-blue-400">{ll.job_ref}</strong></span>}
              {containerEdit ? (
                <span className="flex items-center gap-1">
                  <span className="text-gray-400 text-xs">Container:</span>
                  <input autoFocus value={containerInput} onChange={e => setContainerInput(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter') handleSetContainer(); if (e.key === 'Escape') setContainerEdit(false) }}
                    onBlur={handleSetContainer}
                    className="w-32 text-sm border border-blue-400 rounded px-1.5 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none font-mono" />
                </span>
              ) : (
                <span className="cursor-pointer hover:text-blue-600 group" onClick={() => setContainerEdit(true)}>
                  Container: <strong className="font-mono">{ll.container_no || <span className="text-gray-400 font-normal italic">not set</span>}</strong>
                  <span className="ml-1 text-xs text-blue-400 opacity-0 group-hover:opacity-100">✎</span>
                </span>
              )}
              <span>Uploaded: {fmtDate(ll.uploaded_at)}</span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                ll.parse_status === 'parsed' ? 'bg-green-100 text-green-700' :
                ll.parse_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}>{ll.parse_status}</span>
            </div>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            {saving && <span className="text-xs text-gray-400">Saving…</span>}
            {/* Columns button */}
            <div className="relative">
              <button
                onClick={() => setShowColPicker(p => !p)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Columns ({visibleCols.length}/{ALL_COL_DEFS.length})
              </button>
              {showColPicker && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowColPicker(false)} />
                  <div className="absolute right-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-3 w-48">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Show columns</p>
                    <div className="space-y-0.5">
                      {colOrder.map(k => ALL_COL_DEFS.find(c => c.key === k)!).filter(Boolean).map(col => (
                        <label key={col.key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-1.5 py-1 rounded">
                          <input type="checkbox" className="rounded w-3.5 h-3.5"
                            checked={!hiddenCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                          <span className="text-xs text-gray-700 dark:text-gray-300">{col.label}</span>
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        const d = new Set(DEFAULT_HIDDEN_COLS)
                        const defaultOrder = ALL_COL_DEFS.map(c => c.key)
                        setHiddenCols(d)
                        setColOrder(defaultOrder)
                        localStorage.setItem('ll_hidden_cols', JSON.stringify([...d]))
                        localStorage.setItem('ll_col_order', JSON.stringify(defaultOrder))
                        savePrefsToDb([...d], defaultOrder, sortCol, sortDir)
                      }}
                      className="mt-2 w-full text-xs text-center text-gray-400 hover:text-gray-600 underline"
                    >Reset to defaults</button>
                  </div>
                </>
              )}
            </div>
            {ll.file_url && ll.parse_status !== 'parsed' && (
              <button onClick={handleParseWithAI} disabled={parsing}
                className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                {parsing ? (
                  <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>Parsing…</>
                ) : '✦ Parse with AI'}
              </button>
            )}
            {/* 3-dot menu */}
            {ll.file_url && (
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu(p => !p)}
                  className="flex items-center justify-center w-9 h-9 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-lg leading-none"
                  title="More options"
                >⋮</button>
                {showMoreMenu && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowMoreMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 w-52">
                      <button
                        onClick={() => { setShowMoreMenu(false); handleParseWithAI() }}
                        disabled={parsing}
                        className="w-full text-left px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                      >✦ Re-parse with AI</button>
                      {ll.raw_columns && ll.parse_status === 'parsed' && (
                        <button
                          onClick={() => { setShowMoreMenu(false); handleParseWithAI() }}
                          className="w-full text-left px-4 py-2 text-sm text-blue-700 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >↺ Re-map Columns</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {items.length > 0 && (
              <button onClick={() => setShowSmartSplit(true)}
                className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Smart Devan Split
              </button>
            )}
            {linkedPlan === undefined ? (
              <button disabled className="px-4 py-2 text-sm font-medium bg-gray-400 text-white rounded-lg opacity-60 cursor-wait">
                Checking plan…
              </button>
            ) : linkedPlan === null ? (
              <button onClick={() => setShowGenerate(true)}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">
                Generate Pre-Devanning Plan
              </button>
            ) : linkedPlan.itemCount === 0 ? (
              <button onClick={handleSyncToPlan} disabled={syncingPlan}
                title="Insert items into existing plan from latest LL data"
                className="px-4 py-2 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                {syncingPlan ? 'Syncing…' : 'Sync to Plan'}
              </button>
            ) : (
              <button onClick={handleSyncToPlan} disabled={syncingPlan}
                title={`Plan has ${linkedPlan.itemCount} items — will replace all`}
                className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {syncingPlan ? 'Syncing…' : '⚠ Re-sync to Plan'}
              </button>
            )}
          </div>
        </div>

        {/* ── Column mapping pending banner ── */}
        {ll.raw_columns && ll.parse_status !== 'parsed' && !rawExtract && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg px-4 py-3 text-sm text-yellow-800 dark:text-yellow-300 flex items-center justify-between gap-4">
            <span>⚠ Column mapping required before items can be generated.</span>
            <button onClick={handleParseWithAI} disabled={parsing}
              className="px-3 py-1.5 text-xs font-medium bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 whitespace-nowrap">
              {parsing ? 'Loading…' : 'Map Columns'}
            </button>
          </div>
        )}

        {/* ── Parse error ── */}
        {parseError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex justify-between items-center">
            <span>{parseError}</span>
            <button onClick={() => setParseError(null)} className="text-red-400 hover:text-red-600 ml-4">✕</button>
          </div>
        )}

        {/* ── Total Cartons panel ── */}
        {items.length > 0 && (
          <div className={`rounded-xl border px-5 py-3.5 flex items-center gap-4 flex-wrap ${
            ctnsTarget == null
              ? 'bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800'
              : ctnsDelta === 0
                ? 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-800'
                : 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800'
          }`}>

            {ctnsTarget == null ? (
              /* No target set */
              <>
                <span className="text-sm text-blue-800 dark:text-blue-300">
                  📦 Items sum: <strong>{itemsCtnsTotal}</strong> ctns — no expected total set
                </span>
                {ctnsTargetEdit ? (
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <span className="text-xs text-blue-700 dark:text-blue-400 font-medium whitespace-nowrap">Expected:</span>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-blue-600 dark:text-blue-400">CTNS</label>
                      <input type="number" min="0" step="1" autoFocus value={ctnsTargetInput}
                        onChange={e => setCtnsTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-20 text-sm border border-blue-300 dark:border-blue-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-blue-600 dark:text-blue-400">WT (kg)</label>
                      <input type="number" min="0" step="0.01" value={wtTargetInput}
                        onChange={e => setWtTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-blue-300 dark:border-blue-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-blue-600 dark:text-blue-400">CBM</label>
                      <input type="number" min="0" step="0.001" value={cbmTargetInput}
                        onChange={e => setCbmTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-blue-300 dark:border-blue-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-center" />
                    </div>
                    <button onClick={handleSetCtnsTarget} disabled={saving}
                      className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40">Set</button>
                    <button onClick={() => setCtnsTargetEdit(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setCtnsTargetEdit(true); setCtnsTargetInput(String(itemsCtnsTotal)); setWtTargetInput(ll.expected_total_weight_kg != null ? String(ll.expected_total_weight_kg) : ''); setCbmTargetInput(ll.expected_total_cbm != null ? String(ll.expected_total_cbm) : '') }}
                    className="ml-auto text-xs text-blue-700 dark:text-blue-400 underline hover:no-underline">
                    Set expected total
                  </button>
                )}
              </>
            ) : ctnsDelta === 0 ? (
              /* Match */
              <>
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0114 0z" />
                </svg>
                <span className="text-sm font-semibold text-green-800 dark:text-green-300">Total cartons match ✓</span>
                <span className="text-xs text-green-600 dark:text-green-500">Expected {ctnsTarget} = Items sum {itemsCtnsTotal}</span>
                {ctnsTargetEdit ? (
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <span className="text-xs text-green-700 dark:text-green-400 font-medium whitespace-nowrap">Expected:</span>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-green-600 dark:text-green-400">CTNS</label>
                      <input type="number" min="0" step="1" autoFocus value={ctnsTargetInput}
                        onChange={e => setCtnsTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-20 text-sm border border-green-300 dark:border-green-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-green-600 dark:text-green-400">WT (kg)</label>
                      <input type="number" min="0" step="0.01" value={wtTargetInput}
                        onChange={e => setWtTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-green-300 dark:border-green-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-green-600 dark:text-green-400">CBM</label>
                      <input type="number" min="0" step="0.001" value={cbmTargetInput}
                        onChange={e => setCbmTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-green-300 dark:border-green-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-400 text-center" />
                    </div>
                    <button onClick={handleSetCtnsTarget} disabled={saving}
                      className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40">Save</button>
                    <button onClick={() => setCtnsTargetEdit(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setCtnsTargetEdit(true); setCtnsTargetInput(String(ctnsTarget)); setWtTargetInput(ll.expected_total_weight_kg != null ? String(ll.expected_total_weight_kg) : ''); setCbmTargetInput(ll.expected_total_cbm != null ? String(ll.expected_total_cbm) : '') }}
                    className="ml-auto text-xs text-green-700 dark:text-green-400 underline hover:no-underline">Edit expected</button>
                )}
              </>
            ) : (
              /* Mismatch */
              <>
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm text-amber-800 dark:text-amber-300">Expected: <strong>{ctnsTarget}</strong></span>
                <span className="text-sm text-amber-800 dark:text-amber-300">Items sum: <strong>{itemsCtnsTotal}</strong></span>
                <span className="text-sm font-semibold px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                  {ctnsDelta! > 0 ? `+${ctnsDelta} over` : `${Math.abs(ctnsDelta!)} short`}
                </span>
                {ctnsTargetEdit ? (
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <span className="text-xs text-amber-700 dark:text-amber-400 font-medium whitespace-nowrap">Expected:</span>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-amber-600 dark:text-amber-400">CTNS</label>
                      <input type="number" min="0" step="1" autoFocus value={ctnsTargetInput}
                        onChange={e => setCtnsTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-20 text-sm border border-amber-300 dark:border-amber-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-amber-600 dark:text-amber-400">WT (kg)</label>
                      <input type="number" min="0" step="0.01" value={wtTargetInput}
                        onChange={e => setWtTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-amber-300 dark:border-amber-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-center" />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-amber-600 dark:text-amber-400">CBM</label>
                      <input type="number" min="0" step="0.001" value={cbmTargetInput}
                        onChange={e => setCbmTargetInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSetCtnsTarget(); if (e.key === 'Escape') setCtnsTargetEdit(false) }}
                        className="w-24 text-sm border border-amber-300 dark:border-amber-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-center" />
                    </div>
                    <button onClick={handleSetCtnsTarget} disabled={saving}
                      className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40">Save</button>
                    <button onClick={() => setCtnsTargetEdit(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setCtnsTargetEdit(true); setCtnsTargetInput(String(ctnsTarget)); setWtTargetInput(ll.expected_total_weight_kg != null ? String(ll.expected_total_weight_kg) : ''); setCbmTargetInput(ll.expected_total_cbm != null ? String(ll.expected_total_cbm) : '') }}
                    className="ml-auto text-xs text-amber-700 dark:text-amber-400 underline hover:no-underline">Edit expected</button>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Selection toolbar ── */}
        {items.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center gap-3 flex-wrap">
            <input type="checkbox" className="rounded w-4 h-4 cursor-pointer"
              checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected }}
              onChange={toggleSelectAll} />
            <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[80px]">
              {selectedSet.size > 0 ? `${selectedSet.size} selected` : `${items.length} items`}
            </span>

            {selectedSet.size > 0 && (
              <>
                <button onClick={addToNewGroup}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  + Add to new Devan Split
                </button>
                {groups.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Move to:</span>
                    <select value={moveTarget}
                      onChange={e => { setMoveTarget(e.target.value); if (e.target.value) moveToGroup(e.target.value) }}
                      className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— Select Devan Split —</option>
                      <option value="__unassigned__">Unassigned</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}
                <button onClick={() => setSelectedSet(new Set())} className="text-xs text-gray-400 hover:text-gray-600">
                  Clear selection
                </button>
              </>
            )}

          </div>
        )}

        {/* ── ETA / Free Days compact edit ── */}
        <div className="flex items-center gap-3 flex-wrap text-sm">
          {!etaEdit ? (
            <>
              {ll.container_eta_at && (
                <span className="text-gray-500">
                  ETA: <strong className="text-gray-700 dark:text-gray-200">{fmtDate(ll.container_eta_at)}</strong>
                </span>
              )}
              {ll.free_demurrage_days != null && (
                <span className="text-gray-500">
                  Free days: <strong className="text-gray-700 dark:text-gray-200">{ll.free_demurrage_days}d</strong>
                </span>
              )}
              <button onClick={() => setEtaEdit(true)}
                className="text-xs text-blue-600 hover:underline">
                {ll.container_eta_at ? 'Edit ETA' : 'Set ETA'}
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-gray-500">ETA</label>
              <input type="date" autoFocus value={etaInput} onChange={e => setEtaInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSetEta(); if (e.key === 'Escape') setEtaEdit(false) }}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <label className="text-xs text-gray-500">Free Days</label>
              <input type="number" min="0" max="30" value={freeDaysInput} onChange={e => setFreeDaysInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSetEta(); if (e.key === 'Escape') setEtaEdit(false) }}
                className="w-14 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-center" />
              <button onClick={handleSetEta} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
              <button onClick={() => setEtaEdit(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
            </div>
          )}
        </div>

        {/* ── Items table ── */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...itemsScrollProps}>
            <table ref={itemsTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 w-8" style={{ overflow: 'hidden' }}></th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap" style={{ resize: 'horizontal', overflow: 'hidden' }}>#</th>
                  {visibleCols.map(col => (
                    <th key={col.key}
                      draggable
                      onDragStart={() => handleColDragStart(col.key)}
                      onDragOver={handleColDragOver}
                      onDrop={() => handleColDrop(col.key)}
                      onClick={col.sortable ? () => handleSortClick(col.sortable!) : undefined}
                      style={{ resize: 'horizontal', overflow: 'hidden', cursor: col.sortable ? 'pointer' : 'grab' }}
                      className={`px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap select-none${col.sortable ? ' hover:text-gray-700' : ''}`}>
                      {col.label}{col.sortable && sortCol === col.sortable ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                  <th className="px-3 py-2" style={{ overflow: 'hidden' }}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                {sections.map(section => {
                  const ck = collapseKey(section.id)
                  const isCollapsed = collapsed.has(ck)
                  const allGroupSel = section.indices.length > 0 && section.indices.every(i => selectedSet.has(i))
                  const someGroupSel = section.indices.some(i => selectedSet.has(i)) && !allGroupSel

                  return [
                    <tr key={`hdr-${ck}`} className="bg-slate-100 dark:bg-slate-700/60 select-none">
                      <td colSpan={totalCols} className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          {section.indices.length > 0 && (
                            <input type="checkbox" className="rounded w-3.5 h-3.5 cursor-pointer"
                              checked={allGroupSel}
                              ref={el => { if (el) el.indeterminate = someGroupSel }}
                              onChange={() => toggleGroupSelect(section.indices)} />
                          )}
                          <button onClick={() => setCollapsed(p => { const n = new Set(p); n.has(ck) ? n.delete(ck) : n.add(ck); return n })}
                            className="text-gray-400 hover:text-gray-600 w-4 text-center font-mono text-xs">
                            {isCollapsed ? '▶' : '▼'}
                          </button>
                          {renamingId === section.id && section.id !== null ? (
                            <input autoFocus value={renamingVal}
                              onChange={e => setRenamingVal(e.target.value)}
                              onBlur={commitRename}
                              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null) }}
                              className="text-sm font-semibold bg-white dark:bg-gray-700 border border-blue-400 rounded px-1.5 py-0.5 focus:outline-none w-40" />
                          ) : (
                            <span
                              className={`text-sm font-semibold text-gray-700 dark:text-gray-200 ${section.id ? 'cursor-pointer hover:text-blue-600' : ''}`}
                              onClick={() => section.id && (setRenamingId(section.id), setRenamingVal(section.name))}>
                              {section.name}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">({section.indices.length} items)</span>
                          {section.id && (
                            <div className="flex gap-2 ml-1">
                              <button onClick={() => { setRenamingId(section.id!); setRenamingVal(section.name) }}
                                className="text-xs text-blue-500 hover:text-blue-700 font-medium">Rename</button>
                              <button onClick={() => deleteGroup(section.id!)}
                                className="text-xs text-red-400 hover:text-red-600 font-medium">Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>,

                    ...(!isCollapsed ? section.indices.map(idx => {
                      const item = items[idx]
                      return (
                        <tr key={`item-${idx}`}
                          className={selectedSet.has(idx) ? 'bg-blue-50/60 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                          <td className="px-3 py-1.5 text-center">
                            <input type="checkbox" className="rounded w-3.5 h-3.5 cursor-pointer"
                              checked={selectedSet.has(idx)} onChange={() => toggleItem(idx)} />
                          </td>
                          <td className="px-3 py-1.5 text-gray-400">{idx + 1}</td>
                          {visibleCols.map(col => (
                            <td key={col.key} className="px-1 py-1 align-top">
                              {col.type === 'str' ? (
                                col.wrap ? (
                                  <textarea rows={1}
                                    className={wrapCellCls}
                                    style={wrapStyle}
                                    value={String((item as any)[col.field] || '')}
                                    ref={el => autoHeight(el)}
                                    onInput={e => autoHeight(e.target as HTMLTextAreaElement)}
                                    onChange={e => { autoHeight(e.target); updateItem(idx, col.field, e.target.value || null) }} />
                                ) : (
                                  <input className={cellCls}
                                    value={String((item as any)[col.field] || '')}
                                    onChange={e => updateItem(idx, col.field, e.target.value || null)} />
                                )
                              ) : (
                                <input type="number" step={col.step} min="0"
                                  className="w-16 border border-transparent hover:border-gray-300 focus:border-blue-500 rounded px-1 py-0.5 text-xs text-center bg-transparent dark:text-white focus:outline-none"
                                  value={(item as any)[col.field] ?? ''}
                                  onChange={e => updateItem(idx, col.field, e.target.value ? parseFloat(e.target.value) : null)} />
                              )}
                            </td>
                          ))}
                          <td className="px-3 py-1.5">
                            <button onClick={() => deleteItem(idx)} className="text-red-400 hover:text-red-600">✕</button>
                          </td>
                        </tr>
                      )
                    }) : []),
                  ]
                })}

                {/* Add row */}
                <tr className="bg-blue-50/30 dark:bg-blue-900/10">
                  <td className="px-3 py-1.5 text-gray-300 text-center">+</td>
                  <td className="px-3 py-1.5 text-gray-300">—</td>
                  {visibleCols.map(col => (
                    <td key={col.key} className="px-1 py-1 align-top">
                      {col.type === 'str' ? (
                        col.wrap ? (
                          <textarea rows={1}
                            placeholder={col.field === 'description' ? 'Description *' : ''}
                            className={`w-full min-w-[120px] border rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500 resize-none leading-snug break-words ${col.field === 'description' ? 'border-blue-200 dark:border-blue-700' : 'border-gray-200 dark:border-gray-600'}`}
                            style={wrapStyle}
                            value={String((addRow as any)[col.field] || '')}
                            ref={el => autoHeight(el)}
                            onInput={e => autoHeight(e.target as HTMLTextAreaElement)}
                            onChange={e => { autoHeight(e.target); setAddRow(r => ({ ...r, [col.field]: e.target.value || null })) }} />
                        ) : (
                          <input
                            placeholder={col.field === 'description' ? 'Description *' : ''}
                            className={`w-full min-w-[56px] border rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500 ${col.field === 'description' ? 'border-blue-200 dark:border-blue-700' : 'border-gray-200 dark:border-gray-600'}`}
                            value={String((addRow as any)[col.field] || '')}
                            onChange={e => setAddRow(r => ({ ...r, [col.field]: e.target.value || null }))} />
                        )
                      ) : (
                        <input type="number" step={col.step} min="0"
                          className="w-16 border border-gray-200 dark:border-gray-600 rounded px-1 py-0.5 text-xs text-center bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500"
                          value={(addRow as any)[col.field] ?? ''}
                          onChange={e => setAddRow(r => ({ ...r, [col.field]: e.target.value ? parseFloat(e.target.value) : null }))} />
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5">
                    <button onClick={handleAddRow} disabled={addingRow || !addRow.description.trim()}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40">Add</button>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>
        <StickyHScrollbar {...itemsScrollBar} />
      </div>

      {/* ── Dialogs ── */}
      {showGenerate && (
        <GeneratePlanDialog
          ll={ll} items={items} groups={groups} warehouseId={id!}
          onDone={planIds => {
            setShowGenerate(false)
            navigate(planIds.length === 1 ? `/warehouse/${id}/plans/${planIds[0]}` : `/warehouse/${id}/plans`)
          }}
          onClose={() => setShowGenerate(false)}
        />
      )}

      {parsePreview && (
        <ParsePreviewDialog
          result={parsePreview}
          onConfirm={handleConfirmParse}
          onClose={() => setParsePreview(null)}
        />
      )}

      {rawExtract && savedMappingCustomerName && (
        <div style={{
          position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1100, background: '#1e3a5f', color: '#93c5fd',
          border: '1px solid #2563eb', borderRadius: 8, padding: '10px 18px',
          fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          <span>Applied saved mapping for <strong>{savedMappingCustomerName}</strong>. Click to modify.</span>
          <button
            onClick={() => setSavedMappingCustomerName(null)}
            style={{ background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {rawExtract && (
        <ColumnMappingTable
          columns={rawExtract.columns}
          rawRows={rawExtract.previewRows}
          headerRowIdx={rawExtract.headerRowIdx}
          initialMapping={ll.column_mapping as Record<number, MappingTarget> | undefined}
          onConfirm={handleConfirmMapping}
          onClose={() => setRawExtract(null)}
          confirming={parsing}
        />
      )}

      {showSmartSplit && (
        <SmartDevanSplitDialog
          items={items}
          initialConfig={splitConfig ?? undefined}
          onApply={handleApplySmartSplit}
          onClose={() => setShowSmartSplit(false)}
        />
      )}
    </WarehouseLayout>
  )
}
